import { Visibility, VisibilityOff } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  FormControlLabel,
  Grid2,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import * as Yup from "yup";
import { getAccountUser, updateUser } from "../../api/account";
import { createUser } from "../../api/account/createUser";
import { deleteUser } from "../../api/account/deleteUser";
import { updateUserPassword } from "../../api/account/updateUserPassword";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { Role } from "../../types/interfaces/account";
import {
  CreateUserRequest,
  NEWUSER,
  UpdateUserRequest,
  User,
} from "../../types/interfaces/user";
import { StoreInterfaces } from "../../types/store/StoreInterfaces";
import { getRoleIdFromType } from "../../utils/App";

interface UserDetailViewProps {
  accountId: number;
  administrationAccount: boolean;
  userId: number;
  mode: "create" | "edit" | "view";
  onFinish?: () => void;
  onError?: (error: string) => void;
  readOnly: boolean;
}

interface UserFormValues {
  name: string;
  email: string;
  phoneNumber: string;
  roleId: number;
  enabled: boolean;
  password: string;
  confirmPassword: string;
}

interface PasswordFormValues {
  password: string;
  confirmPassword: string;
}

interface PasswordFieldsProps {
  values: Pick<UserFormValues, "password" | "confirmPassword">;
  errors: Partial<Pick<UserFormValues, "password" | "confirmPassword">>;
  touched: Partial<Record<"password" | "confirmPassword", boolean>>;
  handleChange: React.ChangeEventHandler;
  handleBlur: React.FocusEventHandler;
  formStyles: object;
  t: (key: string) => string;
  fieldNames?: { password: string; confirmPassword: string };
}

const PasswordFields = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  formStyles,
  t,
  fieldNames = { password: "password", confirmPassword: "confirmPassword" },
}: PasswordFieldsProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const passwordKey = fieldNames.password as keyof typeof values;
  const confirmKey = fieldNames.confirmPassword as keyof typeof values;

  const adornment = (show: boolean, toggle: () => void) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={toggle} edge="end">
          {show ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  });

  return (
    <>
      <Grid2 size={6}>
        <TextField
          fullWidth
          type={showPassword ? "text" : "password"}
          id={fieldNames.password}
          name={fieldNames.password}
          label={t("admin.users.password")}
          value={values[passwordKey]}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!(errors[passwordKey] && touched[passwordKey])}
          helperText={errors[passwordKey] || ""}
          sx={{ ...formStyles }}
          InputProps={adornment(showPassword, () => setShowPassword(p => !p))}
        />
      </Grid2>
      <Grid2 size={6}>
        <TextField
          fullWidth
          type={showConfirm ? "text" : "password"}
          id={fieldNames.confirmPassword}
          name={fieldNames.confirmPassword}
          label={t("admin.users.password-confirm")}
          value={values[confirmKey]}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!(errors[confirmKey] && touched[confirmKey])}
          helperText={errors[confirmKey] || ""}
          sx={{ ...formStyles }}
          InputProps={adornment(showConfirm, () => setShowConfirm(p => !p))}
        />
      </Grid2>
    </>
  );
};

const buildUserSchema = (t: (k: string) => string, isCreate: boolean) =>
  Yup.object().shape({
    name: Yup.string()
      .max(80, t("admin.users.user-name-max"))
      .required(t("admin.users.user-name-required")),
    email: Yup.string()
      .email(t("admin.users.user-email-valid"))
      .max(80, t("admin.users.user-email-max"))
      .required(t("admin.users.user-email-required")),
    phoneNumber: Yup.string().required(t("admin.users.user-phone-number-required")),
    roleId: Yup.number().required(t("admin.users.user-role-required")),
    enabled: Yup.boolean().required(t("admin.users.user-enabled-required")),
    ...(isCreate && {
      password: Yup.string().required(t("admin.users.user-password-required")),
      confirmPassword: Yup.string()
        .required(t("admin.users.user-password-confirmation-required"))
        .oneOf([Yup.ref("password")], t("admin.users.user-password-match")),
    }),
  });

const buildPasswordSchema = (t: (k: string) => string) =>
  Yup.object().shape({
    password: Yup.string()
      .min(6, t("admin.users.user-password-min"))
      .required(t("admin.users.user-password-required")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], t("admin.users.user-password-match"))
      .required(t("admin.users.user-password-confirmation-required")),
  });

const UserDetailView = ({
  accountId, 
  administrationAccount, 
  userId,
  mode,
  onFinish,
  onError,
  readOnly,
}: UserDetailViewProps) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();

  const privateSettings = useSelector((state: StoreInterfaces) => state.app.privateSettings);

  const [editMode, setEditMode] = useState(false);
  const [updatePassword, setUpdatePassword] = useState(false);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const formStyles = useFormStyles();

  const showBackButton = !/^\/accounts\/\d+$/.test(location.pathname);
  const showEditButton = mode === "edit" && !editMode && !updatePassword && !readOnly;
  const showSaveActions = mode === "edit" && editMode && !readOnly;
  const showCreateActions = mode === "create" && !readOnly;

  useEffect(() => {
    if (!privateSettings?.roles) {
      setAvailableRoles([]);
      return;
    }
    setAvailableRoles(
      administrationAccount
        ? privateSettings.roles
        : privateSettings.roles.filter(
            role => role.isManager || (!role.isAdministrator && !role.isManager)
          )
    );
  }, [administrationAccount, privateSettings?.roles]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!accountId || accountId === 0) return;
      if (mode === "edit" && (!userId || userId === 0)) return;

      setUser(undefined);

      if (mode === "create") {
        const roleId = getRoleIdFromType("ACCOUNT_USER", privateSettings?.roles);

        if (roleId === undefined) {
          onError?.("base-data-error");
          return;
        }

        setUser({ ...NEWUSER, accountId });
        return;
      }

      dispatch(setLoading(true));

      try {
        const data = await getAccountUser(accountId, userId);
        setUser(data);
      } catch {
        onError?.("fetching");
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchUser();
  }, [accountId, userId]);

  const handleCreate = async (values: UserFormValues) => {
    const request: CreateUserRequest = {
      accountId,
      email: values.email,
      name: values.name,
      password: values.password,
      phoneNumber: values.phoneNumber,
      roleId: values.roleId,
    };

    dispatch(setLoading(true));

    try {
      await createUser(accountId, request);
      onFinish?.();
    } catch (error: any) {
      if (error?.response?.data?.label) {
        enqueueSnackbar(
          t("back-end." + error.response.data.label),
          { variant: "error" }
        );
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleUpdate = async (values: UserFormValues) => {
    const request: UpdateUserRequest = {
      name: values.name,
      email: values.email,
      phoneNumber: values.phoneNumber,
      roleId: values.roleId,
      enabled: values.enabled,
    };

    dispatch(setLoading(true));

    try {
      const response = await updateUser(accountId, userId, request);

      setEditMode(false);
      setUser(response);
      onFinish?.();
    } catch {
      onError?.("update-user-error");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDelete = async () => {
    dispatch(setLoading(true));

    try {
      await deleteUser(accountId, userId);

      enqueueSnackbar(
        t("admin.users.user-delete-success"),
        { variant: "success" }
      );

      onFinish?.();
    } catch {
      enqueueSnackbar(
        t("admin.users.user-delete-error"),
        { variant: "error" }
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleUpdatePassword = async (values: PasswordFormValues) => {
    dispatch(setLoading(true));

    try {
      await updateUserPassword(accountId, userId, {
        password: values.password,
      });

      setUpdatePassword(false);
      onFinish?.();

      enqueueSnackbar(
        t("admin.users.user-update-password-success"),
        { variant: "success" }
      );
    } catch {
      onError?.("update-password-error");

      enqueueSnackbar(
        t("admin.users.user-update-password-fail"),
        { variant: "error" }
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const userInitialValues: UserFormValues = {
    name: user?.name ?? "",
    email: user?.email ?? "",
    phoneNumber: user?.phoneNumber ?? "",
    roleId: user?.roleId ?? 0,
    enabled: user?.enabled ?? true,
    password: "",
    confirmPassword: "",
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      {(mode === "create" || user) && (
        <Formik
          enableReinitialize
          initialValues={userInitialValues}
          validationSchema={buildUserSchema(t, mode === "create")}
          onSubmit={(values) => {
            mode === "create" ? handleCreate(values) : handleUpdate(values);
          }}
        >
          {({ values, touched, errors, handleChange, handleBlur, handleSubmit, resetForm, setFieldValue }) => (
            <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
              <Toolbar sx={{ mb: 3, px: '0 !important' }}>
                <Typography sx={{ flex: "1 1 100%" }} variant="h4">
                  {t("admin.users.user-information")}
                </Typography>

                {showBackButton && (
                  <Tooltip title={t("generic.back")}>
                    <Button onClick={() => navigate(-1)} variant="contained" sx={{ borderRadius: 2 }}>
                      <ArrowBackIcon />
                    </Button>
                  </Tooltip>
                )}
              </Toolbar>

              <Grid2 container spacing={2}>

                {/* Name */}
                <Grid2 size={12}>
                  <TextField
                    fullWidth
                    id="name" name="name"
                    label={t("admin.users.name")}
                    variant={editMode ? "outlined" : "standard"}
                    disabled={mode !== "create" && !editMode}
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.name && touched.name)}
                    helperText={errors.name || ""}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                {/* Email */}
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    id="email" name="email"
                    label={t("admin.users.email")}
                    variant={editMode ? "outlined" : "standard"}
                    disabled={mode !== "create" && !editMode}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.email && touched.email)}
                    helperText={errors.email || ""}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                {/* Phone */}
                <Grid2 size={6}>
                  <Box>
                    <PhoneInput
                      containerClass="my-phone-input"
                      country="pt"
                      disabled={mode !== "create" && !editMode}
                      value={values.phoneNumber}
                      onChange={(phone) =>
                        setFieldValue("phoneNumber", phone.startsWith("+") ? phone : `+${phone}`)
                      }
                      onBlur={() => setFieldValue("phoneNumber", values.phoneNumber, true)}
                      inputStyle={{
                        width: "100%",
                        height: "54px",
                        borderRadius: 8,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        border: `1px solid ${
                          errors.phoneNumber && touched.phoneNumber
                            ? theme.palette.error.main
                            : theme.palette.divider
                        }`,
                        paddingLeft: "48px",
                        boxSizing: "border-box",
                      }}
                      dropdownStyle={{
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        borderRadius: 8,
                        border: `1px solid ${theme.palette.divider}`,
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                    />
                    {errors.phoneNumber && touched.phoneNumber && (
                      <Typography color="error" variant="caption" sx={{ ml: 1.75, mt: 0.5, display: "block" }}>
                        {errors.phoneNumber}
                      </Typography>
                    )}
                  </Box>
                </Grid2>

                {/* Role */}
                <Grid2 size={6}>
                  <TextField
                    fullWidth select
                    id="roleId" name="roleId"
                    label={t("admin.users.role")}
                    variant={editMode ? "outlined" : "standard"}
                    disabled={mode !== "create" && !editMode}
                    value={values.roleId > 0 ? values.roleId : ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.roleId && touched.roleId)}
                    helperText={errors.roleId || ""}
                    sx={{ ...formStyles }}
                  >
                    {availableRoles.map(role => (
                      <MenuItem key={role.id} value={role.id}>
                        {t(role.i18nLabel)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                {/* Enabled */}
                <Grid2 size={6}>
                  <FormControlLabel
                    disabled={mode !== "create" && !editMode}
                    control={
                      <Switch
                        id="enabled" name="enabled"
                        checked={values.enabled}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    }
                    label={t(values.enabled ? "generic.enabled" : "generic.disabled")}
                  />
                </Grid2>

                {/* Password (create only) */}
                {mode === "create" && (
                  <PasswordFields
                    values={{ password: values.password, confirmPassword: values.confirmPassword }}
                    errors={{ password: errors.password, confirmPassword: errors.confirmPassword }}
                    touched={{ password: touched.password, confirmPassword: touched.confirmPassword }}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    formStyles={formStyles}
                    t={t}
                  />
                )}

                {/* Actions */}
                <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                  {showCreateActions && (
                    <>
                      <Button variant="outlined" onClick={() => onFinish?.()}>
                        {t("generic.cancel")}
                      </Button>
                      <Button type="submit" variant="contained">
                        {t("generic.save")}
                      </Button>
                    </>
                  )}

                  {showEditButton && (
                    <>
                      <Button variant="outlined" onClick={() => setEditMode(true)}>
                        {t("admin.accounts.account-edit")}
                      </Button>
                      <Button variant="outlined" onClick={() => setUpdatePassword(true)}>
                        {t("admin.users.user-update-password")}
                      </Button>
                      <Button variant="outlined" color="error" onClick={handleDelete}>
                        {t("admin.users.user-delete")}
                      </Button>
                    </>
                  )}

                  {showSaveActions && (
                    <>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => { resetForm(); setEditMode(false); }}
                      >
                        {t("generic.cancel")}
                      </Button>
                      <Button type="submit" variant="contained">
                        {t("generic.save")}
                      </Button>
                    </>
                  )}
                </Grid2>
              </Grid2>
            </Box>
          )}
        </Formik>
      )}

      {/* Update Password Form */}
      {updatePassword && (
        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          validationSchema={buildPasswordSchema(t)}
          onSubmit={handleUpdatePassword}
        >
          {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => (
            <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
              <Grid2 container spacing={2}>
                <PasswordFields
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  formStyles={formStyles}
                  t={t}
                />

                {/* Actions */}
                <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                  <Button variant="outlined" onClick={() => setUpdatePassword(false)}>
                    {t("generic.cancel")}
                  </Button>
                  <Button type="submit" variant="contained">
                    {t("admin.users.user-update-password")}
                  </Button>
                </Grid2>
              </Grid2>
            </Box>
          )}
        </Formik>
      )}
    </Paper>
  );
};

export default UserDetailView;